import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import { 
  QuestionMarkCircleIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  PaperClipIcon,
  PhotoIcon,
  XMarkIcon,
  DocumentIcon,
  VideoCameraIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import ReactMarkdown from 'react-markdown';
import FileUpload from '../components/FileUpload';

const AdminHelp = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedHelp, setSelectedHelp] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    is_important: false,
    target_audience: ['all']
  });
  const [attachments, setAttachments] = useState([]);
  const [deletedAttachments, setDeletedAttachments] = useState([]);
  const contentRef = useRef(null);

  const { data: helpsData, isLoading } = useQuery(
    ['admin-helps', page, pageSize],
    () => api.get('/helps', { params: { page, limit: pageSize } }).then(res => res.data),
    { keepPreviousData: true }
  );

  const createMutation = useMutation(
    (data) => {
      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('content', data.content);
      formData.append('category', data.category || '');
      formData.append('is_important', data.is_important ? 'true' : 'false');
      formData.append('target_audience', data.target_audience.join(','));
      
      if (data.attachments && data.attachments.length > 0) {
        data.attachments.forEach(file => {
          formData.append('attachments', file);
        });
      }
      
      return api.post('/helps', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    {
      onSuccess: () => {
        toast.success('도움말이 등록되었습니다.');
        queryClient.invalidateQueries('admin-helps');
        setShowCreateModal(false);
        setFormData({ title: '', content: '', category: '', is_important: false, target_audience: ['all'] });
        setAttachments([]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '도움말 등록에 실패했습니다.');
      }
    }
  );

  const updateMutation = useMutation(
    ({ id, data }) => {
      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('content', data.content);
      formData.append('category', data.category || '');
      formData.append('is_important', data.is_important ? 'true' : 'false');
      formData.append('target_audience', data.target_audience.join(','));
      
      if (data.attachments && data.attachments.length > 0) {
        data.attachments.forEach(file => {
          formData.append('attachments', file);
        });
      }
      
      return api.put(`/helps/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    {
      onSuccess: () => {
        toast.success('도움말이 수정되었습니다.');
        queryClient.invalidateQueries('admin-helps');
        setShowEditModal(false);
        setSelectedHelp(null);
        setFormData({ title: '', content: '', category: '', is_important: false, target_audience: ['all'] });
        setAttachments([]);
        setDeletedAttachments([]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '도움말 수정에 실패했습니다.');
      }
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/helps/${id}`),
    {
      onSuccess: () => {
        toast.success('도움말이 삭제되었습니다.');
        queryClient.invalidateQueries('admin-helps');
        setShowDeleteModal(false);
        setSelectedHelp(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '도움말 삭제에 실패했습니다.');
      }
    }
  );

  const helps = helpsData?.helps || [];

  const handleEdit = async (help) => {
    setSelectedHelp(help);
    setFormData({
      title: help.title,
      content: help.content,
      is_important: help.is_important,
      target_audience: help.target_audience ? help.target_audience.split(',') : ['all']
    });
    
    // 상세 정보 가져오기 (첨부파일 포함)
    try {
      const response = await api.get(`/helps/${help.id}`);
      if (response.data.attachments) {
        setAttachments(response.data.attachments);
      }
    } catch (error) {
      console.error('Failed to fetch help details:', error);
    }
    
    setShowEditModal(true);
  };

  const handleDelete = (help) => {
    setSelectedHelp(help);
    setShowDeleteModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = { ...formData, attachments };
    
    if (showEditModal && selectedHelp) {
      // 삭제된 첨부파일 처리
      deletedAttachments.forEach(attachmentId => {
        api.delete(`/helps/${selectedHelp.id}/attachments/${attachmentId}`);
      });
      
      updateMutation.mutate({ id: selectedHelp.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleFileSelect = (files) => {
    setAttachments(prev => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    const attachment = attachments[index];
    if (attachment.id) {
      // 기존 첨부파일인 경우 삭제 목록에 추가
      setDeletedAttachments(prev => [...prev, attachment.id]);
    }
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      const response = await api.post('/helps/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.url;
    } catch (error) {
      toast.error('이미지 업로드에 실패했습니다.');
      throw error;
    }
  };

  const insertImageToContent = async (file) => {
    try {
      const imageUrl = await uploadImage(file);
      const imageMarkdown = `\n![${file.name}](${imageUrl})\n`;
      
      if (contentRef.current) {
        const textarea = contentRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = formData.content;
        const newText = text.substring(0, start) + imageMarkdown + text.substring(end);
        
        setFormData({ ...formData, content: newText });
        
        // 커서 위치 조정
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + imageMarkdown.length;
          textarea.focus();
        }, 0);
      }
    } catch (error) {
      console.error('Failed to insert image:', error);
    }
  };

  const handleTargetAudienceChange = (targetType, isChecked) => {
    if (targetType === 'all') {
      // 전체 선택 시 다른 모든 선택 해제
      setFormData(prev => ({ ...prev, target_audience: isChecked ? ['all'] : [] }));
    } else {
      setFormData(prev => {
        let newTargets = [...prev.target_audience];
        
        if (isChecked) {
          // 'all'이 선택되어 있으면 제거
          newTargets = newTargets.filter(t => t !== 'all');
          newTargets.push(targetType);
        } else {
          newTargets = newTargets.filter(t => t !== targetType);
        }
        
        // 아무것도 선택되지 않았으면 전체 선택
        if (newTargets.length === 0) {
          newTargets = ['all'];
        }
        
        return { ...prev, target_audience: newTargets };
      });
    }
  };

  const insertYoutubeVideo = () => {
    const url = prompt('유튜브 URL을 입력하세요:');
    if (!url) return;

    let videoId = '';
    let isShorts = false;

    // 유튜브 URL 패턴 확인
    if (url.includes('youtube.com/shorts/')) {
      // 쇼츠 URL
      const match = url.match(/shorts\/([^?&]+)/);
      if (match) {
        videoId = match[1];
        isShorts = true;
      }
    } else if (url.includes('youtube.com/watch?v=')) {
      // 일반 유튜브 URL
      const match = url.match(/[?&]v=([^&]+)/);
      if (match) {
        videoId = match[1];
      }
    } else if (url.includes('youtu.be/')) {
      // 단축 URL
      const match = url.match(/youtu\.be\/([^?]+)/);
      if (match) {
        videoId = match[1];
      }
    }

    if (!videoId) {
      toast.error('올바른 유튜브 URL이 아닙니다.');
      return;
    }

    const containerClass = isShorts ? 'youtube-shorts-container' : 'youtube-video-container';
    const embedHTML = `\n<div class="${containerClass}">
  <iframe src="https://www.youtube.com/embed/${videoId}" 
    title="YouTube video player" 
    frameborder="0" 
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
    allowfullscreen>
  </iframe>
</div>\n`;

    if (contentRef.current) {
      const textarea = contentRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.content;
      const newText = text.substring(0, start) + embedHTML + text.substring(end);
      
      setFormData({ ...formData, content: newText });
      
      // 커서 위치 조정
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + embedHTML.length;
        textarea.focus();
      }, 0);
    }
  };

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">도움말 관리</h2>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">표시 개수:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value={25}>25개</option>
              <option value={50}>50개</option>
              <option value={100}>100개</option>
              <option value={200}>200개</option>
            </select>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            새 도움말
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <>
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    번호
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    제목
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작성일
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    조회수
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    관리
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {helps.map((help, index) => (
                  <tr key={help.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {helpsData?.total ? helpsData.total - ((page - 1) * pageSize + index) : index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {help.is_important && (
                          <StarIcon className="h-4 w-4 text-yellow-500" />
                        )}
                        <span className="text-sm font-medium text-gray-900">
                          {help.title}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(help.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {help.view_count || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEdit(help)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(help)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {helpsData && helpsData.pages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  이전
                </button>
                <button
                  onClick={() => setPage(Math.min(helpsData.pages, page + 1))}
                  disabled={page === helpsData.pages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  다음
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    전체 <span className="font-medium">{helpsData.total}</span>개 중{' '}
                    <span className="font-medium">{((page - 1) * pageSize) + 1}</span> -{' '}
                    <span className="font-medium">{Math.min(page * pageSize, helpsData.total)}</span> 표시
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">이전</span>
                      ◀
                    </button>
                    
                    {(() => {
                      const totalPages = helpsData.pages;
                      const maxButtons = 7;
                      let startPage = Math.max(1, page - Math.floor(maxButtons / 2));
                      let endPage = Math.min(totalPages, startPage + maxButtons - 1);
                      
                      if (endPage - startPage < maxButtons - 1) {
                        startPage = Math.max(1, endPage - maxButtons + 1);
                      }
                      
                      const pageButtons = [];
                      
                      if (startPage > 1) {
                        pageButtons.push(
                          <button
                            key={1}
                            onClick={() => setPage(1)}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                          >
                            1
                          </button>
                        );
                        if (startPage > 2) {
                          pageButtons.push(
                            <span key="ellipsis1" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                              ...
                            </span>
                          );
                        }
                      }
                      
                      for (let i = startPage; i <= endPage; i++) {
                        pageButtons.push(
                          <button
                            key={i}
                            onClick={() => setPage(i)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              i === page
                                ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {i}
                          </button>
                        );
                      }
                      
                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pageButtons.push(
                            <span key="ellipsis2" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                              ...
                            </span>
                          );
                        }
                        pageButtons.push(
                          <button
                            key={totalPages}
                            onClick={() => setPage(totalPages)}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                          >
                            {totalPages}
                          </button>
                        );
                      }
                      
                      return pageButtons;
                    })()}
                    
                    <button
                      onClick={() => setPage(Math.min(helpsData.pages, page + 1))}
                      disabled={page === helpsData.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">다음</span>
                      ▶
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-bold mb-4">
                {showEditModal ? '도움말 수정' : '새 도움말 작성'}
              </h3>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      제목
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      내용 (마크다운 지원)
                    </label>
                    <div className="flex gap-2 mb-2">
                      <label className="cursor-pointer flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                        <PhotoIcon className="h-4 w-4" />
                        <span className="text-sm">사진</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              insertImageToContent(e.target.files[0]);
                            }
                          }}
                          className="hidden"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={insertYoutubeVideo}
                        className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        <PlayIcon className="h-4 w-4" />
                        <span className="text-sm">유튜브</span>
                      </button>
                    </div>
                    <textarea
                      ref={contentRef}
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      rows={10}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      대상 사용자 (복수 선택 가능)
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.target_audience.includes('all')}
                          onChange={(e) => handleTargetAudienceChange('all', e.target.checked)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">전체</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.target_audience.includes('contractor')}
                          onChange={(e) => handleTargetAudienceChange('contractor', e.target.checked)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">소장</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.target_audience.includes('customer')}
                          onChange={(e) => handleTargetAudienceChange('customer', e.target.checked)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">고객</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.target_audience.includes('vip_customer')}
                          onChange={(e) => handleTargetAudienceChange('vip_customer', e.target.checked)}
                          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">VIP 고객</span>
                      </label>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_important"
                      checked={formData.is_important}
                      onChange={(e) => setFormData({ ...formData, is_important: e.target.checked })}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_important" className="text-sm text-gray-700">
                      중요 도움말로 표시
                    </label>
                  </div>
                  
                  {/* 첨부파일 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      첨부파일
                    </label>
                    <div className="space-y-2">
                      <FileUpload
                        accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.hwp,.zip,.txt"
                        multiple
                        onFileSelect={handleFileSelect}
                        maxSize={50 * 1024 * 1024} // 50MB
                      />
                      
                      {attachments.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {attachments.map((file, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                              <div className="flex items-center gap-2">
                                {file.type?.startsWith('image/') ? (
                                  <PhotoIcon className="h-5 w-5 text-gray-400" />
                                ) : file.type?.startsWith('video/') ? (
                                  <VideoCameraIcon className="h-5 w-5 text-gray-400" />
                                ) : (
                                  <DocumentIcon className="h-5 w-5 text-gray-400" />
                                )}
                                <span className="text-sm text-gray-700">
                                  {file.name || file.file_name}
                                </span>
                                {file.size && (
                                  <span className="text-xs text-gray-500">
                                    ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => removeAttachment(index)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <XMarkIcon className="h-5 w-5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setShowEditModal(false);
                      setFormData({ title: '', content: '', category: '', is_important: false, target_audience: ['all'] });
                      setAttachments([]);
                      setDeletedAttachments([]);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {createMutation.isLoading || updateMutation.isLoading ? '처리중...' : '저장'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              <h3 className="text-lg font-bold">도움말 삭제</h3>
            </div>
            <p className="text-gray-600 mb-6">
              "{selectedHelp.title}" 도움말을 삭제하시겠습니까?<br />
              이 작업은 되돌릴 수 없습니다.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedHelp(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                취소
              </button>
              <button
                onClick={() => deleteMutation.mutate(selectedHelp.id)}
                disabled={deleteMutation.isLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isLoading ? '삭제중...' : '삭제'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHelp;